import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw } from 'lucide-react';

const TextCaptcha = ({ onChange, onExpired }) => {
  const [captchaText, setCaptchaText] = useState('');
  const [userAnswer, setUserAnswer] = useState('');
  const canvasRef = useRef(null);

  // Generate random alphanumeric string with mixed case
  const generateCaptchaText = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'; // Removed confusing chars like I, O, 0, 1, l
    let text = '';
    for (let i = 0; i < 6; i++) {
      text += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return text;
  };

  // Draw captcha on canvas
  const drawCaptcha = (text) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Background with noise
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, width, height);

    // Add noise dots
    for (let i = 0; i < 100; i++) {
      ctx.fillStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.3)`;
      ctx.fillRect(Math.random() * width, Math.random() * height, 2, 2);
    }

    // Add noise lines
    for (let i = 0; i < 5; i++) {
      ctx.strokeStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.3)`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * width, Math.random() * height);
      ctx.lineTo(Math.random() * width, Math.random() * height);
      ctx.stroke();
    }

    // Draw text with distortion
    ctx.font = 'bold 40px Arial';
    ctx.textBaseline = 'middle';

    const charSpacing = width / (text.length + 1);
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const x = charSpacing * (i + 1);
      const y = height / 2;

      // Random rotation
      const angle = (Math.random() - 0.5) * 0.4;
      
      // Random color
      const colors = ['#2c5f2d', '#1e3a8a', '#7c2d12', '#4c1d95', '#134e4a'];
      ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)];

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      
      // Random vertical offset
      const yOffset = (Math.random() - 0.5) * 10;
      ctx.fillText(char, 0, yOffset);
      
      ctx.restore();
    }
  };

  // Generate new captcha
  const generateNewCaptcha = () => {
    const newText = generateCaptchaText();
    setCaptchaText(newText);
    setUserAnswer('');
    drawCaptcha(newText);
    
    // Clear the token when captcha changes
    if (onChange) {
      onChange(null);
    }
  };

  // Initialize captcha
  useEffect(() => {
    generateNewCaptcha();
  }, []);

  // Handle user input - case sensitive
  const handleInputChange = (e) => {
    const value = e.target.value;
    setUserAnswer(value);

    // Check if answer is correct (case-sensitive)
    if (value.trim() !== '' && value === captchaText) {
      // Generate a token
      const token = btoa(`captcha_${Date.now()}_${captchaText}`);
      if (onChange) {
        onChange(token);
      }
    } else {
      if (onChange) {
        onChange(null);
      }
    }
  };

  // Refresh captcha
  const handleRefresh = () => {
    generateNewCaptcha();
    if (onExpired) {
      onExpired();
    }
  };

  return (
    <div className="border border-jet-gray-300 rounded-md p-4 bg-gray-50">
      <div className="flex items-center justify-between mb-3">
        <label className="text-sm font-medium text-jet-gray-700">
          Security Check
        </label>
        <button
          type="button"
          onClick={handleRefresh}
          className="text-jet-gray-500 hover:text-jet-gray-700 transition-colors"
          title="Refresh captcha"
        >
          <RefreshCw size={16} />
        </button>
      </div>
      
      <div className="mb-3">
        {/* Canvas for captcha image */}
        <div className="bg-white border border-jet-gray-300 rounded mb-3 flex items-center justify-center">
          <canvas
            ref={canvasRef}
            width={300}
            height={80}
            className="max-w-full"
          />
        </div>

        <label className="text-sm font-medium text-jet-gray-800 mb-1 block">
          What code is in the image?
        </label>
        <input
          type="text"
          value={userAnswer}
          onChange={handleInputChange}
          placeholder="Enter the characters (without spaces)"
          maxLength={6}
          style={{ textTransform: 'none' }}
          className="w-full px-3 py-2 border border-jet-gray-300 rounded-md text-sm bg-white text-jet-gray-800 placeholder:text-jet-gray-400 transition-colors duration-300 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]"
        />
        <p className="text-xs text-jet-gray-500 mt-1">
          Enter the characters exactly as shown (case-sensitive).
        </p>
      </div>
    </div>
  );
};

export default TextCaptcha;
